import type { IntlShape } from "react-intl";
import type { FavoriteDomain } from "@/graphql/generated/operations";

// Labels for a catalogue item's tags. Each domain has its own short code list
// (a film genre, a continent, a cuisine); the bundles carry one label per code
// under grow.games.toplist.tag.<DOMAIN>.<CODE>, and they are the only list
// this client keeps. A code without a label is left out rather than shown raw.
export function tagLabels(intl: IntlShape, domain: FavoriteDomain, codes: readonly string[]): string[] {
  return codes.flatMap((code) => {
    const id = `grow.games.toplist.tag.${domain}.${code}`;
    return id in intl.messages ? [intl.formatMessage({ id })] : [];
  });
}

// The colour of each tag of a domain, as a tile hue, so entries of one category
// share a colour while there are no pictures. The codes come from the bundle,
// sorted so a reordered bundle moves no colour, and are spread evenly round the
// wheel: a hash of the codes put two movie genres 2 degrees apart.
export function tagHues(intl: IntlShape, domain: FavoriteDomain): ReadonlyMap<string, number> {
  const prefix = `grow.games.toplist.tag.${domain}.`;
  const codes = Object.keys(intl.messages)
    .filter((id) => id.startsWith(prefix))
    .map((id) => id.slice(prefix.length))
    .sort();
  return new Map(codes.map((code, i) => [code, Math.round((i * 360) / codes.length)]));
}

// The hue of an entry: that of its first tag with a colour, as the first label
// on its card names it. Undefined for an entry without one.
export function firstTagHue(hues: ReadonlyMap<string, number>, codes: readonly string[]): number | undefined {
  return codes.map((code) => hues.get(code)).find((hue) => hue !== undefined);
}

// A catalogue item's year as shown under its title. Books reach back before
// year one (the Iliad is -800), so a negative year reads "800 v. Chr." / "800 BC".
export function releaseYearLabel(intl: IntlShape, year: number | null | undefined): string | undefined {
  if (year == null) return undefined;
  return year < 0
    ? intl.formatMessage({ id: "grow.games.toplist.yearBc", defaultMessage: "{year} BC" }, { year: -year })
    : String(year);
}
